/**
 * PTEL Tool - Servicio de Re-validación de Registros (Scoring v2)
 * Validación local rápida tras edición manual de coordenadas
 *
 * Migrado de scoring ad-hoc a pipeline D1+D2+D3 → ScoreResult.
 *
 * @version 2.0.0
 * @task SC.08
 * @date 2026-02-15
 */

import { validarDistanciaCentroide } from '../../lib/CoordinateValidator';
import type { Infraestructura } from '../../components/Step2/types';
import type { ScoreResult, ScoreNivel, D3ValidacionInput as _D3ValidacionInput } from '../../types/scoring';
import { computeD1Origen } from '../scoring/d1Origen';
import { computeD2Precision } from '../scoring/d2Precision';
import { computeD3Validacion, buildD3Input } from '../scoring/d3Validacion';
import { computeFinalScore } from '../scoring/finalScore';

// ============================================================================
// TYPES
// ============================================================================

export interface RevalidationResult {
  success: boolean;

  /** Score v2 detallado con D1/D2/D3, nivel y flags */
  scoreDetail: ScoreResult;

  /** Score total 0-100 (alias de scoreDetail.total) */
  newScore: number;
  previousScore: number;
  scoreDelta: number;

  /** Detalles de validación espacial (backwards compat) */
  validationDetails: {
    coordenadasValidas: boolean;
    dentroAndalucia: boolean;
    dentroRadioMunicipal: boolean;
    distanciaKm: number | null;
  };
  message: string;
  recomendaciones: string[];
}

// ============================================================================
// CONSTANTS
// ============================================================================

/**
 * Límites UTM30 ETRS89 para Andalucía (EPSG:25830)
 */
const ANDALUCIA_BOUNDS = {
  minX: 100000,
  maxX: 620000,
  minY: 3980000,
  maxY: 4290000,
};

/** Radio máximo por defecto para validación municipal (km) */
const RADIO_MUNICIPAL_KM = 20;

// ============================================================================
// SCORING v2 HELPERS
// ============================================================================

/**
 * Construye ScoreResult mínimo para coords inválidas (null/NaN).
 * Devuelve score 0, nivel NULA, flag SIN_COORDENADAS.
 */
function buildEmptyScoreResult(): ScoreResult {
  const d1 = computeD1Origen({
    cascadeLevel: 'L0', source: 'manual', offline: false, geocoderUsed: 'manual',
  });
  const d2 = computeD2Precision({
    matchType: undefined, fuzzyScore: undefined,
    resolution: 'unknown', fromDocument: false,
  });
  const d3 = computeD3Validacion(buildD3Input(undefined, undefined, 0, undefined));

  // Override: forzar total=0 para coords inválidas
  return {
    total: 0,
    nivel: 'NULA' as ScoreNivel,
    d1, d2, d3,
    flags: ['SIN_COORDENADAS'],
    weights: { d1: 0.35, d2: 0.40, d3: 0.25 },
    timestamp: new Date().toISOString(),
  };
}

/**
 * Evalúa validación espacial: bounds Andalucía + centroide municipal.
 */
function evaluateSpatial(
  x: number, y: number, codIne: string | undefined, municipio: string | undefined,
): {
  dentroAndalucia: boolean;
  dentroRadio: boolean;
  distanciaKm: number | null;
  spatialValid: boolean | undefined;
  recomendaciones: string[];
} {
  const recomendaciones: string[] = [];

  const dentroAndalucia = (
    x >= ANDALUCIA_BOUNDS.minX && x <= ANDALUCIA_BOUNDS.maxX &&
    y >= ANDALUCIA_BOUNDS.minY && y <= ANDALUCIA_BOUNDS.maxY
  );

  if (!dentroAndalucia) {
    recomendaciones.push('Las coordenadas están fuera de los límites de Andalucía');
    recomendaciones.push(`Rango válido X: ${ANDALUCIA_BOUNDS.minX}-${ANDALUCIA_BOUNDS.maxX}`);
    recomendaciones.push(`Rango válido Y: ${ANDALUCIA_BOUNDS.minY}-${ANDALUCIA_BOUNDS.maxY}`);
    return { dentroAndalucia, dentroRadio: false, distanciaKm: null, spatialValid: false, recomendaciones };
  }

  // Centroide municipal
  let dentroRadio = false;
  let distanciaKm: number | null = null;

  if (codIne) {
    const centResult = validarDistanciaCentroide(x, y, codIne, RADIO_MUNICIPAL_KM);
    if (centResult.exito) {
      distanciaKm = centResult.distanciaKm;
      dentroRadio = centResult.dentroRadio;
      if (!centResult.dentroRadio) {
        recomendaciones.push(
          `Coordenada a ${centResult.distanciaKm}km del centro de ${municipio ?? codIne}`,
        );
        recomendaciones.push('Verifica que las coordenadas corresponden al municipio correcto');
      }
    } else {
      recomendaciones.push('No se pudo verificar la distancia al centro municipal');
    }
  } else {
    recomendaciones.push('Sin código INE para validar municipio');
  }

  // spatialValid = dentro de Andalucía Y (dentro radio O sin centroide disponible)
  const spatialValid = dentroAndalucia && (dentroRadio || distanciaKm === null);

  return { dentroAndalucia, dentroRadio, distanciaKm, spatialValid, recomendaciones };
}

// ============================================================================
// MAIN FUNCTION
// ============================================================================

/**
 * Re-valida un registro tras edición manual de coordenadas.
 * Usa pipeline scoring v2 (D1+D2+D3→ScoreResult).
 *
 * Para edición manual:
 * - D1 Origen: source='manual', cascadeLevel='L0' (usuario es la autoridad)
 * - D2 Precisión: resolution='exact' (UTM explícito), fuzzy=1.0
 * - D3 Validación: bounds Andalucía + centroide municipal
 */
export function revalidateRecord(
  record: Pick<Infraestructura, 'x' | 'y' | 'codIne' | 'municipio' | 'score'>,
): RevalidationResult {
  const { x, y, codIne, municipio, score: previousScore } = record;
  const prev = previousScore || 0;

  // ── Coords inválidas: retorno temprano ──
  if (x === null || y === null || x === undefined || y === undefined) {
    const scoreDetail = buildEmptyScoreResult();
    return {
      success: false, scoreDetail, newScore: 0, previousScore: prev,
      scoreDelta: -prev, message: 'Coordenadas no proporcionadas',
      validationDetails: {
        coordenadasValidas: false, dentroAndalucia: false,
        dentroRadioMunicipal: false, distanciaKm: null,
      },
      recomendaciones: ['Introduce coordenadas X e Y válidas'],
    };
  }

  if (typeof x !== 'number' || typeof y !== 'number' || isNaN(x) || isNaN(y)) {
    const scoreDetail = buildEmptyScoreResult();
    return {
      success: false, scoreDetail, newScore: 0, previousScore: prev,
      scoreDelta: -prev, message: 'Coordenadas no son números válidos',
      validationDetails: {
        coordenadasValidas: false, dentroAndalucia: false,
        dentroRadioMunicipal: false, distanciaKm: null,
      },
      recomendaciones: ['Las coordenadas deben ser valores numéricos'],
    };
  }

  // ── Validación espacial ──
  const spatial = evaluateSpatial(x, y, codIne, municipio);

  // ── D1 Origen: edición manual = usuario como fuente directa ──
  const d1 = computeD1Origen({
    cascadeLevel: 'L0',
    source: 'manual',
    offline: false,
    geocoderUsed: 'manual',
  });

  // ── D2 Precisión: UTM explícito → resolución exacta ──
  const d2 = computeD2Precision({
    matchType: 'exact',
    fuzzyScore: 1.0,
    resolution: 'exact',
    fromDocument: false,
  });

  // ── D3 Validación: resultados espaciales ──
  const d3 = computeD3Validacion(
    buildD3Input(spatial.spatialValid, undefined, 0, undefined),
  );

  // ── Score final ──
  const scoreDetail = computeFinalScore({
    d1, d2, d3,
    hasOriginalCoords: false,  // Edición manual, no coords de documento
    isGeocoded: false,         // No es geocodificación, es edición directa
  });

  const newScore = scoreDetail.total;
  const scoreDelta = newScore - prev;

  // ── Mensaje según nivel v2 ──
  const message = buildMessage(scoreDetail.nivel, scoreDelta);

  return {
    success: true,
    scoreDetail,
    newScore,
    previousScore: prev,
    scoreDelta,
    validationDetails: {
      coordenadasValidas: true,
      dentroAndalucia: spatial.dentroAndalucia,
      dentroRadioMunicipal: spatial.dentroRadio,
      distanciaKm: spatial.distanciaKm,
    },
    message,
    recomendaciones: spatial.recomendaciones,
  };
}

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

/** Genera mensaje descriptivo según nivel y delta */
function buildMessage(nivel: ScoreNivel, scoreDelta: number): string {
  const nivelMessages: Record<ScoreNivel, string> = {
    CRITICA: 'Coordenadas válidas - Confianza crítica',
    ALTA:    'Coordenadas válidas - Alta confianza',
    MEDIA:   'Coordenadas válidas - Confianza media',
    BAJA:    'Coordenadas con advertencias',
    NULA:    'Coordenadas requieren revisión',
  };

  let msg = nivelMessages[nivel];

  if (scoreDelta > 0) {
    msg += ` (+${scoreDelta} puntos)`;
  } else if (scoreDelta < 0) {
    msg += ` (${scoreDelta} puntos)`;
  }
  return msg;
}

/**
 * Verifica si las coordenadas cambiaron respecto al registro original
 */
export function coordinatesChanged(
  newX: number | null | undefined,
  newY: number | null | undefined,
  originalX: number | null | undefined,
  originalY: number | null | undefined,
): boolean {
  if ((newX === null || newX === undefined) && (originalX === null || originalX === undefined)) {
    if ((newY === null || newY === undefined) && (originalY === null || originalY === undefined)) {
      return false;
    }
  }
  return newX !== originalX || newY !== originalY;
}

/**
 * Formatea el resultado de re-validación para mostrar en toast.
 * Usa ScoreNivel del scoring v2 para determinar variante.
 */
export function getToastMessage(result: RevalidationResult): {
  variant: 'success' | 'warning' | 'error';
  title: string;
  description: string;
} {
  const { nivel } = result.scoreDetail;

  if (nivel === 'CRITICA' || nivel === 'ALTA') {
    return {
      variant: 'success',
      title: '✓ Coordenadas válidas',
      description: `Score: ${result.newScore}/100 [${nivel}] - ${result.message}`,
    };
  } else if (nivel === 'MEDIA' || nivel === 'BAJA') {
    return {
      variant: 'warning',
      title: '⚠ Coordenadas con advertencias',
      description: `Score: ${result.newScore}/100 [${nivel}] - ${result.recomendaciones[0] || result.message}`,
    };
  } else {
    return {
      variant: 'error',
      title: '✗ Revisar coordenadas',
      description: `Score: ${result.newScore}/100 [${nivel}] - ${result.message}`,
    };
  }
}

// ============================================================================
// EXPORTS
// ============================================================================

export default revalidateRecord;
